/**
 * Per-deck keyboard shortcuts: map Slides page `objectId` → KeyboardEvent.code.
 * Stored on the pack as `deckSlideShortcuts[presentationId][objectId] = code`.
 */

/** Codes reserved for built-in deck / control navigation (must stay in sync with App keyboard handlers). */
export const RESERVED_SLIDE_SHORTCUT_CODES = new Set([
  'Space',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Period',
  'Tab',
  'Escape',
  'Enter',
  'KeyT',
  'KeyB',
  'KeyW',
]);

/**
 * Plain single-key bindings only (no Ctrl/Meta/Alt) so we don’t fight the browser.
 * @param {KeyboardEvent} e
 * @param {{ allowBackspaceDelete?: boolean }} [opts]
 */
export function isSlideShortcutBindingAllowed(e, opts = {}) {
  if (!e || e.repeat) return false;
  if (e.ctrlKey || e.metaKey || e.altKey) return false;
  if (opts.allowBackspaceDelete && (e.code === 'Backspace' || e.code === 'Delete')) return true;
  if (e.code === 'Backspace' || e.code === 'Delete') return false;
  if (RESERVED_SLIDE_SHORTCUT_CODES.has(e.code)) return false;
  if (/^F\d{1,2}$/.test(e.code)) return false;
  return true;
}

/** Human-readable label for a badge (physical key, layout-stable). */
export function formatShortcutBadgeLabel(code) {
  if (!code || typeof code !== 'string') return '';
  if (code.startsWith('Key') && code.length === 4) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Numpad')) return code.replace('Numpad', '');
  if (code === 'Minus') return '−';
  if (code === 'Equal') return '=';
  if (code === 'BracketLeft') return '[';
  if (code === 'BracketRight') return ']';
  if (code === 'Backslash') return '\\';
  if (code === 'Semicolon') return ';';
  if (code === 'Quote') return "'";
  if (code === 'Comma') return ',';
  if (code === 'Slash') return '/';
  if (code === 'Backquote') return '`';
  return code;
}

/**
 * Coerce presentation + page keys to strings so lookups survive JSON round-trips
 * and id type mismatches (string vs number).
 * @param {Record<string, Record<string, string>> | undefined} all
 * @returns {Record<string, Record<string, string>>}
 */
export function normalizeDeckSlideShortcuts(all) {
  const root = {};
  if (!all || typeof all !== 'object') return root;
  for (const [pidRaw, deckRaw] of Object.entries(all)) {
    const pid = String(pidRaw);
    if (!deckRaw || typeof deckRaw !== 'object') continue;
    const deck = {};
    for (const [oidRaw, code] of Object.entries(deckRaw)) {
      if (typeof code !== 'string' || !code) continue;
      deck[String(oidRaw)] = code;
    }
    if (Object.keys(deck).length > 0) root[pid] = deck;
  }
  return root;
}

/**
 * Merge Drive manifest shortcuts with in-memory shortcuts. Local wins on key collisions
 * so bindings are not dropped if a folder sync read a slightly stale manifest before a save landed.
 * @param {Record<string, Record<string, string>> | undefined} localAll
 * @param {Record<string, Record<string, string>> | undefined} driveAll
 */
export function mergeDeckSlideShortcutsPreferLocal(localAll, driveAll) {
  const local = normalizeDeckSlideShortcuts(localAll);
  const drive = normalizeDeckSlideShortcuts(driveAll);
  const ids = new Set([...Object.keys(local), ...Object.keys(drive)]);
  const out = {};
  for (const pid of ids) {
    const p = String(pid);
    out[p] = { ...(drive[p] || {}), ...(local[p] || {}) };
  }
  return out;
}

/** @param {Record<string, Record<string, string>> | undefined} all */
export function getDeckSlideShortcutCode(all, presentationId, objectId) {
  const norm = normalizeDeckSlideShortcuts(all);
  const pid = String(presentationId || '');
  const oid = String(objectId || '');
  const deck = norm[pid];
  if (!deck) return undefined;
  return deck[oid];
}

/**
 * @param {Record<string, Record<string, string>> | undefined} all
 * @param {string} presentationId
 * @param {string} objectId
 * @param {string | null | undefined} code
 * @returns {Record<string, Record<string, string>>}
 */
export function applyDeckSlideShortcutBinding(all, presentationId, objectId, code) {
  const pid = String(presentationId || '');
  const oid = String(objectId || '');
  const root = normalizeDeckSlideShortcuts(all);
  if (!pid || !oid) return root;

  if (!code) {
    if (!root[pid]) return root;
    const d = { ...root[pid] };
    delete d[oid];
    if (Object.keys(d).length === 0) delete root[pid];
    else root[pid] = d;
    return root;
  }

  for (const k of Object.keys(root)) {
    const deck = { ...(root[k] || {}) };
    let changed = false;
    for (const [o, v] of Object.entries(deck)) {
      if (v === code && !(k === pid && String(o) === oid)) {
        delete deck[o];
        changed = true;
      }
    }
    if (changed) {
      if (Object.keys(deck).length === 0) delete root[k];
      else root[k] = deck;
    }
  }

  const next = { ...(root[pid] || {}) };
  next[oid] = code;
  root[pid] = next;
  return root;
}

/** @param {Record<string, Record<string, string>> | undefined} all */
export function buildSlideJumpCodeIndex(all, presentationId) {
  const norm = normalizeDeckSlideShortcuts(all);
  const pid = String(presentationId || '');
  const deck = norm[pid];
  if (!deck || typeof deck !== 'object') return new Map();
  const m = new Map();
  for (const [objectId, c] of Object.entries(deck)) {
    if (typeof c === 'string' && c) m.set(c, String(objectId));
  }
  return m;
}
